import { useEffect, useMemo, useState } from "react";
import { api } from "../api";
import { navigate } from "../router";
import { haptic } from "../telegram";
import { useGameSocket } from "../useGameSocket";
import { Board } from "../board/Board";
import { ConnectionBadge } from "./Lobby";

const COLOR_LABEL: Record<string, string> = {
  RED: "قرمز",
  BLUE: "آبی",
  GREEN: "سبز",
  YELLOW: "زرد",
};

export function GamePage({ roomCode }: { roomCode: string }) {
  const { status, state, send, lastError } = useGameSocket(roomCode);
  const [myId, setMyId] = useState<number | null>(null);
  const [rolling, setRolling] = useState(false);

  useEffect(() => {
    api
      .auth()
      .then((r) => setMyId(r.user.telegramId))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (state?.roomStatus === "LOBBY") navigate("lobby", { room: roomCode });
  }, [state?.roomStatus, roomCode]);

  const me = state?.players.find((p) => p.userId === myId) ?? null;
  const currentTurn = state?.turn;
  const isMyTurn = currentTurn?.playerId === myId;
  const canRoll = isMyTurn && currentTurn?.status === "WAITING_FOR_ROLL";
  const canMove = isMyTurn && currentTurn?.status === "WAITING_FOR_PIECE";

  const winner = useMemo(
    () => state?.players.find((p) => p.userId === state.winnerUserId) ?? null,
    [state],
  );

  const roll = () => {
    if (!canRoll) return;
    haptic("heavy");
    setRolling(true);
    send({ type: "ROLL_DICE" });
    setTimeout(() => setRolling(false), 600);
  };

  const move = (pieceId: string) => {
    if (!canMove) return;
    haptic("medium");
    send({ type: "MOVE_PIECE", pieceId });
  };

  const leave = () => {
    send({ type: "LEAVE_ROOM" });
    navigate("home");
  };

  const rematch = async () => {
    try {
      const res = await api.createRoom();
      navigate("lobby", { room: res.roomCode });
    } catch {
      navigate("home");
    }
  };

  if (!state) {
    return (
      <div className="screen center">
        <div className="spinner" />
        <ConnectionBadge status={status} />
      </div>
    );
  }

  if (state.roomStatus === "FINISHED") {
    return (
      <div className="screen center">
        <div className="card center">
          <h1>🏁 پایان بازی</h1>
          {winner && (
            <p className="winner">
              🏆 {winner.firstName ?? winner.username ?? "بازیکن"} ({COLOR_LABEL[winner.color]}) برد!
            </p>
          )}
          <button className="btn primary block" onClick={rematch}>
            🎮 Rematch
          </button>
          <button className="btn block" onClick={() => navigate("home")}>
            🏠 منوی اصلی
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="screen game">
      <header className="header row">
        <ConnectionBadge status={status} />
        <span className="muted" dir="ltr">
          v{state.version}
        </span>
      </header>

      <div className="players-strip">
        {state.players.map((p) => (
          <div
            key={p.userId}
            className={`chip ${p.color.toLowerCase()} ${currentTurn?.playerId === p.userId ? "active" : ""}`}
          >
            {p.firstName ?? p.username ?? p.userId}
            {p.userId === myId && " (شما)"}
            <span className="muted"> {p.finishedPieces}/4</span>
          </div>
        ))}
      </div>

      <Board
        pieces={state.pieces}
        validMoves={canMove ? state.validMoves : []}
        onPieceClick={move}
        myColor={me?.color ?? null}
      />

      <div className="card center">
        {isMyTurn ? (
          <div className="your-turn">🎯 نوبت شماست</div>
        ) : (
          <div className="muted">
            نوبت: {state.players.find((p) => p.userId === currentTurn?.playerId)?.firstName ?? "…"}
          </div>
        )}

        <div className={`dice ${rolling ? "rolling" : ""}`} dir="ltr">
          {currentTurn?.diceResult ?? "–"}
        </div>

        {canRoll && (
          <button className="btn primary block" onClick={roll}>
            🎲 انداختن تاس
          </button>
        )}
        {canMove && state.validMoves.length === 0 && (
          <p className="muted">حرکت مجازی وجود ندارد</p>
        )}
        {canMove && state.validMoves.length > 0 && (
          <p className="muted">روی مهره هایلایت‌شده بزنید</p>
        )}

        <button className="btn danger block" onClick={leave}>
          🚪 خروج از بازی
        </button>
      </div>

      {lastError && <div className="toast error">{lastError.message}</div>}
    </div>
  );
}
